import queryString from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Spinner } from 'components/ui';
import { useCurrentCustomerId } from 'utils/account/hooks';
import { useAuthContext } from 'utils/auth';
import { notifyBugsnag, setBugsnagUser } from 'utils/bugsnag';
import { setHeapUser } from 'utils/heap';
import { setLogRocketUser } from 'utils/logrocket';

import { APP_NAME } from '../env';
import { clear, getItem } from '../storage';

import { extendAuthUserRetention, rememberAuthUser, withAuthDiagnostics } from './authDiagnostics';

interface SessionProps {
  children: React.ReactElement;
}

function Session({ children }: SessionProps) {
  const { error, isAuthenticated, isLoading, logout, user } = useAuthContext();
  const [currentCustomerId, setCurrentCustomerId] = useCurrentCustomerId();
  const [initialized, setInitialized] = useState<boolean>(false);
  const reportedLogoutRef = useRef(false);
  const rememberedSubRef = useRef<string>();
  const redirectUrl = useRedirectUrl();

  const hasAppAccess = useMemo(() => {
    const groups: string[] = user?.['https://skyspecs.com/jwt/ohi']?.groups ?? [];
    return isLoading ? false : groups.includes(APP_NAME);
  }, [isLoading, user]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      // FIXME Jo 8/28/25 - Adding debug statements to help debug unexplained logouts. Remove these once the mystery has been solved.
      if (!isAuthenticated) {
        console.debug('Session: User is not authenticated. Navigating to redirectUrl.');
      }
      if (!user) {
        console.debug('Session: User did not resolve. Navigating to redirectUrl.');
      }
      // the effect can re-run before the page leaves, report only once
      if (!reportedLogoutRef.current) {
        reportedLogoutRef.current = true;
        notifyBugsnag(
          'Session: logging out.',
          withAuthDiagnostics({ isAuthenticated, hasUser: Boolean(user), error })
        );
      }

      clear();
      return window.location.replace(redirectUrl);
    }

    // set before the no-access logout so its report is not attributed to a stale stored user
    setBugsnagUser(user);

    if (!hasAppAccess) {
      // to avoid infinite redirects, we want to explicitly log the user out
      // when they don't have Horizon access
      console.info('User does not have Horizon access');
      // FIXME: Remove debug after logout problem is solved
      console.debug('Session: User does not have app access. Calling logout.');
      logout({ broadcast: true });
      return;
    }

    // Attempt to preserve the customer that the user had switched into
    if (!currentCustomerId) {
      setCurrentCustomerId(
        getItem('customerId') || user['https://skyspecs.com/jwt/flt']?.defaultOrg
      );
    }

    // the effect re-runs on navigation and customer switches, keep the session start time
    if (rememberedSubRef.current !== user.sub) {
      rememberedSubRef.current = user.sub;
      rememberAuthUser(user);
    } else {
      extendAuthUserRetention();
    }
    setLogRocketUser(user);
    setHeapUser(user);

    setInitialized(true);
  }, [
    currentCustomerId,
    error,
    hasAppAccess,
    isAuthenticated,
    isLoading,
    logout,
    redirectUrl,
    setCurrentCustomerId,
    setInitialized,
    user,
  ]);

  if (isAuthenticated && initialized) {
    return children;
  }

  return <Spinner />;
}

export default Session;

function useRedirectUrl() {
  const { createHref, location } = useHistory();
  const { pathname, search: lsearch, state: lstate } = location;

  // the `returnTo` and `state` values are read in AuthRedirect
  const search =
    queryString.stringify({
      returnTo: pathname === '/' && !lsearch ? undefined : `${pathname}${lsearch}`,
      state:
        // ignore state when all values are falsy
        !lstate || (typeof lstate === 'object' && !Object.values(lstate).some(Boolean))
          ? undefined
          : JSON.stringify(lstate),
    }) || undefined;

  return createHref({ pathname: '/auth_redirect', search });
}
